import RadioSelect from 'components/shared/radio-select'
import * as R from 'ramda'

const TIMEZONE_OPTIONS = [
  { value: -10, label: 'Hawaii time' },
  { value: -9, label: 'Alaska time' },
  { value: -8, label: 'Pacific time' },
  { value: -7, label: 'Mountain time' },
  { value: -6, label: 'Central time' },
  { value: -5, label: 'Eastern time' },
  { value: -4, label: 'Atlantic time' },
  { value: -3, label: 'Newfoundland time' },
]

export const findMatchingTzLabel = offset =>
  R.propOr(
    null,
    'label',
    R.find(R.propEq('value', parseInt(offset)), TIMEZONE_OPTIONS)
  )

export const findOffsetForTzLabel = label =>
  R.propOr(null, 'value', R.find(R.propEq('label', label), TIMEZONE_OPTIONS))

const parseOffsetForDate = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  }).formatToParts(date)
  const value = parts.find(p => p.type === 'timeZoneName')?.value
  if (!value) return null
  if (value === 'GMT' || value === 'UTC') return 0
  const match = value.match(/GMT([+-])(\d+)(?::(\d+))?/)
  if (!match) return null
  const sign = match[1] === '-' ? -1 : 1
  return sign * parseInt(match[2], 10)
}

export const getCurrentOffsetForTimeZone = timeZone => {
  if (!timeZone) return null
  try {
    return parseOffsetForDate(new Date(), timeZone)
  } catch {
    return null
  }
}

// Standard (non-DST) offset, used for label lookup. A timezone's standard
// offset is the smaller of its January and July offsets — for northern zones
// the winter (Jan) offset is standard, for southern zones the winter (July)
// offset is standard. Using min(jan, july) handles both hemispheres so e.g.
// America/Santiago resolves to -4 ("Atlantic time"), its real standard
// offset, rather than -3 (its January/DST offset).
export const findMatchingTzLabelForTimeZone = timeZone => {
  if (!timeZone) return null
  try {
    const year = new Date().getFullYear()
    const jan = parseOffsetForDate(new Date(year, 0, 1), timeZone)
    const jul = parseOffsetForDate(new Date(year, 6, 1), timeZone)
    if (jan == null && jul == null) return null
    if (jan == null) return findMatchingTzLabel(jul)
    if (jul == null) return findMatchingTzLabel(jan)
    return findMatchingTzLabel(Math.min(jan, jul))
  } catch {
    return null
  }
}

// Canonical IANA timezone for each label, so when we merge zones under a
// shared label (e.g. America/Denver + America/Phoenix → "Mountain time")
// we can display a single header time that follows that label's DST rules,
// rather than picking whichever client happens to be first in the group.
const LABEL_TO_CANONICAL_TZ = {
  'Hawaii time': 'Pacific/Honolulu',
  'Alaska time': 'America/Anchorage',
  'Pacific time': 'America/Los_Angeles',
  'Mountain time': 'America/Denver',
  'Central time': 'America/Chicago',
  'Eastern time': 'America/New_York',
  'Atlantic time': 'America/Halifax',
  'Newfoundland time': 'America/St_Johns',
}

export const getCanonicalTimeZoneForLabel = label =>
  LABEL_TO_CANONICAL_TZ[label] || null

const UtcOffsetPicker = ({ value, onChange }) => {
  return (
    <RadioSelect options={TIMEZONE_OPTIONS} value={value} onChange={onChange} />
  )
}

export default UtcOffsetPicker
